import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  isClubManager,
  isSuperAdmin,
  hasAnyPermission,
  type User,
} from '../utils/roleBasedAccess';
import ClubManagerDashboard from './ClubManagerDashboard';
import SuperAdminDashboard from './Dashboards/SuperAdminDashboard';

const RoleBasedDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const [noAccessibleModule, setNoAccessibleModule] = useState(false);

  // Memoize userData so it can be safely used in hook dependencies
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );

  const isClubManagerUser = isClubManager(userData);
  const isSuperAdminUser = isSuperAdmin(userData);

  // Only these permissions qualify a user to actually *see* the dashboard
  // (matches the tournament-registrations-dashboard module permissions).
  const canSeeDashboard = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.view',
    'tournament.read',
    'tournament.registrations.view',
    'tournament.registrations.manage',
    'tournament.dashboard.view',
  ]);

  const rawPermissions: any = permissions;
  const permissionsLoaded =
    rawPermissions && Array.isArray(rawPermissions.permissionCodes);

  // When on "/dashboard" (sidebar click) and the user has dashboard-qualifying
  // permissions, show the dashboard. On "/" (login/home), always redirect.
  const isExplicitDashboard = location.pathname === '/dashboard';
  const skipRedirect =
    isClubManagerUser || isSuperAdminUser || (canSeeDashboard && isExplicitDashboard);

  useEffect(() => {
    if (!permissionsLoaded || skipRedirect) {
      return;
    }

    const redirectToFirstAvailable = () => {
      // 1. Access Management
      // Route to the most specific section the user can access.
      if (
        hasAnyPermission(userData, [
          'access_management.admin',
          'access_management.view',
          'access_management.users.view',
          'access_management.users.manage',
        ])
      ) {
        navigate('/access-management/users', { replace: true });
        return;
      }
      if (
        hasAnyPermission(userData, [
          'access_management.roles.view',
          'access_management.roles.manage',
        ])
      ) {
        navigate('/access-management/roles', { replace: true });
        return;
      }
      if (
        hasAnyPermission(userData, [
          'access_management.permissions.view',
          'access_management.permissions.manage',
        ])
      ) {
        navigate('/access-management/permissions', { replace: true });
        return;
      }
      if (
        hasAnyPermission(userData, [
          'access_management.audit_logs.view',
        ])
      ) {
        navigate('/audit-logs', { replace: true });
        return;
      }

      // 2. Feature Management
      // - feature_management.view / admin / features.*: go to Features list
      // - only feature_management.feature_toggles.*: go directly to Feature Toggles list
      if (
        hasAnyPermission(userData, [
          'feature_management.view',
          'feature_management.admin',
          'feature_management.features.view',
          'feature_management.features.manage',
        ])
      ) {
        navigate('/feature-management/features', { replace: true });
        return;
      }
      if (
        hasAnyPermission(userData, [
          'feature_management.feature_toggles.view',
          'feature_management.feature_toggles.manage',
        ])
      ) {
        navigate('/feature-management/feature-toggles', { replace: true });
        return;
      }

      // 3. Tournament modules (priority within tournaments)
      // 3.1 Full / listing access (admin + read-only)
      if (
        hasAnyPermission(userData, [
          'tournament.admin',
          'tournament.manage',
          'tournament.tournaments.view',
          'tournament.read',
        ])
      ) {
        navigate('/tournaments-listing', { replace: true });
        return;
      }

      // 3.2 Registrations & registration details (including generic tournament.read)
      if (
        hasAnyPermission(userData, [
          'tournament.registrations.view',
          'tournament.registrations.manage',
          'tournament.registration_details.manage',
          'tournament.read',
        ])
      ) {
        navigate('/registered-tournaments', { replace: true });
        return;
      }

      // 3.3 Tournament dashboard
      if (hasAnyPermission(userData, ['tournament.dashboard.view'])) {
        navigate('/dashboard', { replace: true });
        return;
      }

      // 3.4 Club-level within tournaments
      if (
        hasAnyPermission(userData, [
          'club.read',
          'club.manage',
        ])
      ) {
        navigate('/all-clubs', { replace: true });
        return;
      }

      // 3.5 Players management
      if (
        hasAnyPermission(userData, [
          'tournament.players.view',
          'tournament.players.manage',
        ])
      ) {
        navigate('/manage-players', { replace: true });
        return;
      }

      // 3.6 Payments
      if (hasAnyPermission(userData, ['tournament.payments.view'])) {
        navigate('/super-admin-payments', { replace: true });
        return;
      }

      // 3.7 Fixtures management
      if (hasAnyPermission(userData, ['tournament.fixtures.manage'])) {
        navigate('/manage-tournaments-fixtures', { replace: true });
        return;
      }

      // 3.8 Generic read-only tournament access
      if (hasAnyPermission(userData, ['tournament.view', 'tournament.read'])) {
        navigate('/registered-tournaments', { replace: true });
        return;
      }

      // 4. Shop Services
      if (
        hasAnyPermission(userData, [
          'shop.view',
          'shop.admin',
          'shop.seller.view',
          'shop.seller.manage',
          'shop.product.view',
          'shop.product.manage',
          'shop.order.view',
          'shop.order.manage',
        ])
      ) {
        const hasSellerOrProductAccess = hasAnyPermission(userData, [
          'shop.view',
          'shop.admin',
          'shop.seller.view',
          'shop.seller.manage',
          'shop.product.view',
          'shop.product.manage',
        ]);
        const hasOnlyOrdersAccess =
          hasAnyPermission(userData, ['shop.order.view', 'shop.order.manage']) &&
          !hasSellerOrProductAccess;

        if (hasOnlyOrdersAccess) {
          navigate('/shop-services/orders', { replace: true });
        } else {
          navigate('/shop-services/sellers', { replace: true });
        }
        return;
      }

      // 5. Promos
      if (
        hasAnyPermission(userData, [
          'promo.view',
          'promo.admin',
          'promo.campaigns.view',
          'promo.campaigns.manage',
          'promo.audit_logs.view',
        ])
      ) {
        // If user only has audit-logs permission, go directly to audit logs.
        const hasCampaignsAccess = hasAnyPermission(userData, [
          'promo.view',
          'promo.admin',
          'promo.campaigns.view',
          'promo.campaigns.manage',
        ]);
        const hasOnlyAuditLogsAccess =
          hasAnyPermission(userData, ['promo.audit_logs.view']) &&
          !hasCampaignsAccess;

        if (hasOnlyAuditLogsAccess) {
          navigate('/promos/audit-logs', { replace: true });
        } else {
          navigate('/promos/campaigns', { replace: true });
        }
        return;
      }

      // 6. Academy Management
      // 6.1 Enrolment access → go to enrolments list
      if (
        hasAnyPermission(userData, [
          'academy_management.admin',
          'academy_management.view',
          'academy_management.enrollments.view',
          'academy_management.enrollments.manage',
        ])
      ) {
        navigate('/academy-enrolments', { replace: true });
        return;
      }
      // 6.2 Trials-only access → go directly to Add Trial
      if (hasAnyPermission(userData, ['academy_management.trials.manage'])) {
        navigate('/academy-trials/add', { replace: true });
        return;
      }

      // 7. No accessible module found – mark and do not navigate
      setNoAccessibleModule(true);
    };

    redirectToFirstAvailable();
  }, [permissionsLoaded, skipRedirect, navigate, userData]);

  // For club managers, show the comprehensive dashboard
  if (isClubManagerUser) {
    return <ClubManagerDashboard />;
  }

  // For platform admin users, show the super admin dashboard.
  // Also show it for users who explicitly navigated to /dashboard and have
  // dashboard-qualifying tournament permissions.
  if (isSuperAdminUser || (canSeeDashboard && isExplicitDashboard)) {
    return <SuperAdminDashboard />;
  }

  // If user has no accessible modules, show a clear message instead of a spinner
  if (permissionsLoaded && noAccessibleModule) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <p className="mb-2 fw-semibold">No modules available</p>
          <p className="text-muted mb-0">
            Your account does not have access to any sections. Please contact an administrator.
          </p>
        </div>
      </div>
    );
  }

  // Show loading while redirecting for other users
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
      <div className="text-center">
        <Spinner animation="border" role="status" className="mb-3">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Redirecting to appropriate dashboard...</p>
      </div>
    </div>
  );
};

export default RoleBasedDashboard; 